/**
 * Canvas 2D 绘制封装：固定逻辑分辨率 VIEW_W×VIEW_H，letterbox 适配窗口。
 * 所有坐标均为逻辑坐标。
 */
import { VIEW_W, VIEW_H } from '../config.js';

export class Renderer {
  /**
   * @param {HTMLCanvasElement} canvas
   */
  constructor(canvas) {
    this.canvas = canvas;
    this._ctx = canvas.getContext('2d');
    this._scale = 1;
    this._shakeFrames = 0;
    this._shakeMag = 0;
    this._shakeX = 0;
    this._shakeY = 0;
    this.resize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => this.resize());
    }
  }

  /**
   * 按显示尺寸 × devicePixelRatio 提高画布分辨率（HiDPI 渲染）：
   * 文字与图形按设备像素高清绘制；像素 sprite 关闭平滑保持锐利。
   * 逻辑坐标系不变（VIEW_W×VIEW_H），绘制代码无需改动。
   */
  resize() {
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const h = this.canvas.clientHeight || VIEW_H;
    this._scale = Math.max(1, (h * dpr) / VIEW_H);
    this.canvas.width = Math.round(VIEW_W * this._scale);
    this.canvas.height = Math.round(VIEW_H * this._scale);
    this._ctx.imageSmoothingEnabled = false;
  }

  /** 2D 上下文（需要自定义绘制时用） */
  get ctx() {
    return this._ctx;
  }

  /** 清屏并应用震屏偏移，开启一帧绘制。 */
  begin() {
    const ctx = this._ctx;
    const s = this._scale;
    ctx.setTransform(s, 0, 0, s, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    if (this._shakeFrames > 0) {
      // 按真实帧间隔折算 60Hz 帧数：调用方传入的是逻辑帧语义，
      // 直接每 rAF 减 1 会让震屏时长随显示频率漂移（120Hz 减半/30Hz 翻倍——历史 bug）
      const now = performance.now();
      const dt = this._lastBeginT !== undefined ? (now - this._lastBeginT) / 1000 : 1 / 60;
      this._lastBeginT = now;
      this._shakeFrames = Math.max(0, this._shakeFrames - dt * 60);
      this._shakeX = (Math.random() * 2 - 1) * this._shakeMag;
      this._shakeY = (Math.random() * 2 - 1) * this._shakeMag;
      if (this._shakeFrames === 0) {
        this._shakeX = 0;
        this._shakeY = 0;
      }
    }
    ctx.setTransform(s, 0, 0, s, this._shakeX * s, this._shakeY * s);
  }

  /** 结束一帧绘制（还原变换）。 */
  end() {
    this._ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /**
   * 画精灵图。
   * @param {HTMLImageElement|HTMLCanvasElement} img
   * @param {number} x @param {number} y 锚点位置（默认中心）
   * @param {{rot?:number, scale?:number, alpha?:number, anchorX?:number, anchorY?:number, flipX?:boolean, composite?:string}} [opts]
   */
  sprite(img, x, y, { rot = 0, scale = 1, alpha = 1, anchorX = 0.5, anchorY = 0.5, flipX = false, composite } = {}) {
    const ctx = this._ctx;
    ctx.save();
    ctx.translate(x, y);
    if (rot !== 0) ctx.rotate(rot);
    if (flipX) ctx.scale(-scale, scale);
    else if (scale !== 1) ctx.scale(scale, scale);
    if (alpha !== 1) ctx.globalAlpha = alpha;
    if (composite) ctx.globalCompositeOperation = composite;
    if (img.__atlas) {
      // atlas 帧包装（tools/build.mjs 产物，main.js 装配）：src-rect 绘制
      ctx.drawImage(img.img, img.sx, img.sy, img.w, img.h, -img.w * anchorX, -img.h * anchorY, img.w, img.h);
    } else {
      ctx.drawImage(img, -img.width * anchorX, -img.height * anchorY);
    }
    ctx.restore();
  }

  /**
   * 画矩形。
   * @param {number} x @param {number} y @param {number} w @param {number} h
   * @param {{fill?:string, stroke?:string, alpha?:number}} [opts]
   */
  rect(x, y, w, h, { fill, stroke, alpha = 1 } = {}) {
    const ctx = this._ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fillRect(x, y, w, h);
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.strokeRect(x, y, w, h);
    }
    ctx.restore();
  }

  /**
   * 画圆。
   * @param {number} x @param {number} y @param {number} r
   * @param {{fill?:string, stroke?:string, alpha?:number}} [opts]
   */
  circle(x, y, r, { fill, stroke, alpha = 1, lineWidth = 1 } = {}) {
    const ctx = this._ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * 画文字。默认使用仓库内置 Fusion Pixel 字体（css @font-face 加载，
   * main.js 启动时等待就绪；加载失败回退 monospace）。
   * @param {string} str
   * @param {number} x @param {number} y
   * @param {{size?:number, color?:string, align?:CanvasTextAlign, font?:string, alpha?:number}} [opts]
   */
  text(str, x, y, { size = 16, color = '#fff', align = 'left', font = '"Fusion Pixel", monospace', alpha = 1 } = {}) {
    const ctx = this._ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = `${size}px ${font}`;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = 'top';
    ctx.fillText(str, x, y);
    ctx.restore();
  }

  /** 测量文本宽度（px）：用于按内容宽度布局（箭头/语言行等）。 */
  measure(str, { size = 16, font = '"Fusion Pixel", monospace' } = {}) {
    const ctx = this._ctx;
    ctx.save();
    ctx.font = `${size}px ${font}`;
    const w = ctx.measureText(str).width;
    ctx.restore();
    return w;
  }

  /**
   * 精灵换色（像素美学：buff 状态直接画在角色身上，不叠 UI 滤镜）：
   * offscreen 画布绘制精灵后以 source-atop 填色，缓存复用；支持 atlas 帧包装。
   * @param {HTMLImageElement|HTMLCanvasElement|object} img 原图或 atlas 帧包装
   * @param {string} color 目标色（如 '#f66'）
   * @param {number} [strength=0.5] 着色强度 0..1
   * @returns {HTMLCanvasElement}
   */
  tinted(img, color, strength = 0.5) {
    if (!this._tintCache) this._tintCache = new Map();
    const isAtlas = !!img.__atlas;
    const src = isAtlas ? img.img : img;
    const key = `${color}|${strength}`;
    let per = this._tintCache.get(src);
    if (!per) {
      per = new Map();
      this._tintCache.set(src, per);
    }
    const ck = isAtlas ? `${img.sx},${img.sy},${img.w},${img.h}|${key}` : key;
    if (per.has(ck)) return per.get(ck);
    const w = isAtlas ? img.w : src.width;
    const h = isAtlas ? img.h : src.height;
    const cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    const cx = cv.getContext('2d');
    cx.imageSmoothingEnabled = false;
    if (isAtlas) cx.drawImage(src, img.sx, img.sy, img.w, img.h, 0, 0, w, h);
    else cx.drawImage(src, 0, 0);
    cx.globalCompositeOperation = 'source-atop';
    cx.globalAlpha = strength;
    cx.fillStyle = color;
    cx.fillRect(0, 0, w, h);
    per.set(ck, cv);
    return cv;
  }

  /**
   * 触发屏幕震动。
   * @param {number} frames 持续帧数
   * @param {number} magnitude 最大偏移像素
   */
  shake(frames, magnitude) {
    // 合并策略取强度大者（mag×frames）：后触发的小震不截断未结束的大震
    if (magnitude * frames >= (this._shakeMag || 0) * (this._shakeFrames || 0)) {
      this._shakeFrames = frames;
      this._shakeMag = magnitude;
    }
  }
}
